import { useState, useMemo } from 'react';
import style from '../styles/Social.module.css';
import { trpc } from '../utils/trpc';
import GroupCard from './GroupCard';
import { EventCard } from '../components/EventCard';

export function Social() {
  const [filter, setFilter] = useState<'groups' | 'events'>('groups');

  /** Profile query */
  const { data: profile, isLoading: profileLoading } =
    trpc.member.getMyProfile.useQuery();

  /** Groups query */
  const { data: groupMemberships, isLoading: groupsLoading } =
    trpc.group.listMine.useQuery();

  /** Extract groups */
  const groups = useMemo(() => {
    if (!groupMemberships || !Array.isArray(groupMemberships)) return [];
    return groupMemberships.flatMap((membership) => {
      const groupsArray = Array.isArray(membership.groups)
        ? membership.groups
        : [membership.groups];

      return groupsArray
        .filter((group) => group?.id && group?.name)
        .map((group) => ({
          id: group.id,
          name: group.name,
          description: group.description,
          privacy: group.privacy,
          banner_url: group.banner_url,
          profile_picture_url: group.profile_picture_url,
          role: membership.role,
        }));
    });
  }, [groupMemberships]);

  /** Events query (NOW AUTH-INDEPENDENT) */
  const { data: suggestedEvents = [], isLoading } = trpc.event.byDate.useQuery(
    {
      limit: 10,
    },
    {
      enabled: !!profile?.id,
    },
  );

  /** Loading guard */
  if (profileLoading || groupsLoading) {
    return <p>Loading profile...</p>;
  }

  return (
    <div className={style.page}>
      <div className={style.pageControls}>
        <div className={style.titleRow}>
          <div className={style.pageTitle}>Social</div>

          <div className={style.controlsRight}>
            <div className={style.filterTabs}>
              {(['groups', 'events'] as const).map((f) => (
                <button
                  key={f}
                  className={`${style.filterTab} ${filter === f ? style.activeTab : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f === 'groups' ? 'Groups' : 'Events'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {filter === 'groups' ? (
        <div className="groups-section">
          {groups.length > 0 ? (
            <div className="groups-container">
              {groups.map((group) => (
                <GroupCard
                  key={group.id}
                  group={group}
                  canEdit={group.role === 'admin' || group.role === 'owner'}
                />
              ))}
            </div>
          ) : (
            <div className="empty-groups">
              <p>You haven't joined any groups yet.</p>
            </div>
          )}
        </div>
      ) : filter === 'events' ? (
        isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={style.skeletonCard}>
              <div className={style.skeletonTitle}></div>
              <div className={style.skeletonText}></div>
            </div>
          ))
        ) : suggestedEvents.length === 0 ? (
          <p>No results found.</p>
        ) : (
          suggestedEvents.map((event: any) => (
            <EventCard key={event.event_id} event={event} groupId={event.group_id} />
          ))
        )
      ) : null}
    </div>
  );
}
